import { useEffect, useState } from "react";
import { Plus, Trash2, MoreHorizontal } from "lucide-react";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from "@/components/ui/context-menu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GenerateDialog } from "@/components/GenerateDialog";

const Item = ({ label, count, active, onClick, id, onDelete }) => (
  <div
    data-testid={id}
    onClick={onClick}
    className={`group flex items-center justify-between px-6 py-2.5 cursor-pointer text-[17px] ${active ? "bg-slate-200/80 font-semibold" : "hover:bg-slate-100"}`}
  >
    <span className="truncate uppercase tracking-tight">{label}</span>
    <span className="flex items-center gap-2 text-slate-600">
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500" data-testid={`${id}-delete`}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {count}
    </span>
  </div>
);

export const groupActions = (e, i, total, admin, act) => [
  ...(e.g ? [["edit", "Modifier", () => act.rename(e.g), true], ["delete", "Supprimer", () => act.remove(e.g), admin]] : []),
  ["move-to", "Déplacer vers la position", () => act.moveTo(e, i, total), total > 1],
  ["move-up", "Déplacer vers le haut", () => act.move(i, i - 1), i > 0],
  ["move-down", "Déplacer vers le bas", () => act.move(i, i + 1), i < total - 1],
  ...(e.g ? [["clone", "Cloner", () => act.clone(e.g), true]] : []),
];

// Right-click (or "•••") menu on a group, like ScreenConnect.
const GroupItem = ({ e, i, total, admin, act, ...props }) => {
  const items = groupActions(e, i, total, admin, act);
  const id = e.id;
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="relative group/gi">
          <Item id={id} {...props} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button onClick={(e) => e.stopPropagation()} data-testid={`${id}-more`} className="absolute right-14 top-1/2 -translate-y-1/2 opacity-0 group-hover/gi:opacity-100 text-slate-500 hover:text-slate-900"><MoreHorizontal className="h-5 w-5" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {items.map(([k, label, fn, ok]) => <DropdownMenuItem key={k} disabled={!ok} onSelect={fn} data-testid={`${id}-menu-${k}`}>{label}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-60" data-testid={`${id}-context-menu`}>
        {items.map(([k, label, fn, ok]) => <ContextMenuItem key={k} disabled={!ok} onSelect={fn} data-testid={`${id}-ctx-${k}`}>{label}</ContextMenuItem>)}
      </ContextMenuContent>
    </ContextMenu>
  );
};

// Access filters + groups in the organisation's order, with their actions (shared by ScreenView 1 and 2).
export const useGroupEntries = ({ machines, groups, onSelect, onChanged, helpCount = 0, outdatedCount = 0 }) => {
  const count = (fn) => machines.filter(fn).length;
  const [order, setOrder] = useState([]);
  useEffect(() => { api.get("/settings/access-order").then(({ data }) => setOrder(data.order)).catch(() => {}); }, []);
  const none = count((m) => !m.group_id);
  const defaults = [
    { key: "f:help", id: "group-help", sel: "help", label: "Demandes d'aide", n: helpCount, count: helpCount ? <span className="rounded-full bg-red-600 text-white text-xs font-semibold px-2 py-0.5">{helpCount}</span> : 0 },
    { key: "f:outdated", id: "group-outdated", sel: "outdated", label: "Agents à mettre à jour", n: outdatedCount, count: outdatedCount ? <span className="rounded-full bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5">{outdatedCount}</span> : 0 },
    { key: "f:sessions", id: "group-sessions", sel: "sessions", label: "Sessions en cours", count: count((m) => m.in_session) },
    { key: "f:all", id: "group-all", sel: "all", label: "Toutes les machines", count: machines.length },
    { key: "f:online", id: "group-online", sel: "online", label: "En ligne", count: count((m) => m.online) },
    ...groups.map((g) => ({ key: `g:${g.group_id}`, id: `group-${g.group_id}`, sel: g.group_id, label: g.name, g, count: count((m) => m.group_id === g.group_id) })),
    ...(none > 0 ? [{ key: "f:none", id: "group-none", sel: "none", label: "Non classé", count: none }] : []),
  ];
  const rank = (e, i) => { const k = order.indexOf(e.key); return k >= 0 ? k : 10000 + i; };
  const entries = defaults.map((e, i) => [rank(e, i), e]).sort((a, b) => a[0] - b[0]).map(([, e]) => e);

  const add = (name) => api.post("/groups", { name }).then(({ data }) => { onChanged(); onSelect(data.group_id); return true; }).catch((err) => { toast.error(errMsg(err)); return false; });
  const remove = async (g) => {
    if (!window.confirm(`Supprimer le groupe ${g.name} ? Les machines passent en « Non classé ».`)) return;
    await api.delete(`/groups/${g.group_id}`);
    onSelect("all");
    onChanged();
  };
  const call = (p, ok) => p.then((r) => { if (ok) toast.success(ok); onChanged(); return r; }).catch((e) => toast.error(errMsg(e)));
  const act = {
    remove,
    rename: (g) => { const n = window.prompt("Nouveau nom du groupe", g.name); if (n && n.trim() && n.trim() !== g.name) call(api.put(`/groups/${g.group_id}`, { name: n.trim() }), "Groupe renommé"); },
    move: (from, to) => {
      const keys = entries.map((x) => x.key);
      const [k] = keys.splice(from, 1);
      keys.splice(to, 0, k);
      setOrder(keys);
      api.put("/settings/access-order", { order: keys }).catch((err) => toast.error(errMsg(err)));
    },
    moveTo: (e, i, total) => {
      const n = Number(window.prompt(`Nouvelle position de « ${e.label} » (1 à ${total})`, String(i + 1)));
      if (Number.isInteger(n) && n >= 1 && n <= total) act.move(i, n - 1);
    },
    clone: (g) => call(api.post(`/groups/${g.group_id}/clone`), "Groupe cloné").then((r) => r?.data && onSelect(r.data.group_id)),
  };
  return { entries, act, add };
};

export const GroupColumn = ({ machines, groups, selected, onSelect, onChanged, helpCount = 0, outdatedCount = 0 }) => {
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const { entries, act, add: addGroup } = useGroupEntries({ machines, groups, onSelect, onChanged, helpCount, outdatedCount });
  const add = async (e) => {
    e.preventDefault();
    if (await addGroup(name)) { setName(""); setAdding(false); }
  };
  return (
    <section className="w-[340px] flex-shrink-0 border-r border-slate-200 flex flex-col min-h-0" data-testid="group-column">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-3xl font-medium mb-3">Access</h1>
        <p className="text-[15px] text-slate-700 mb-5">Installez un agent et connectez-vous aux périphériques sans assistance.</p>
        <GenerateDialog />
      </div>
      <div className="flex-1 overflow-y-auto">
        {entries.map((e, i) => (
          <GroupItem key={e.key} e={e} i={i} total={entries.length} admin={user?.role === "admin"} act={act}
            label={e.label} count={e.count} active={selected === e.sel} onClick={() => onSelect(e.sel)} />
        ))}
      </div>
      <div className="border-t border-slate-200 p-4">
        {adding ? (
          <form onSubmit={add} className="flex gap-2">
            <Input autoFocus data-testid="group-name-input" placeholder="Nom de l'entreprise" value={name} onChange={(e) => setName(e.target.value)} />
            <Button data-testid="group-add-btn" type="submit" disabled={!name.trim()}>OK</Button>
          </form>
        ) : (
          <button data-testid="create-group-btn" onClick={() => setAdding(true)} className="w-full flex items-center justify-center gap-1 text-[15px] text-slate-700 hover:text-blue-600">
            <Plus className="h-4 w-4" /> Créer un groupe de sessions
          </button>
        )}
      </div>
    </section>
  );
};
